// Lightweight, framework-agnostic cookie-consent store (GDPR/ePrivacy MVP).
//
// Scope: a single binary "tracking" consent for EU/EEA/UK visitors. Non-essential
// analytics (the landing experiment, GA4, Google Ads) only run when
// `hasTrackingConsent()` is true. Essential cookies (`role`, `country`,
// `emailForSignIn`, `language`) are exempt and are not governed here.
//
// Geo model:
//   - Positively-detected EU/EEA/UK → consent required; trackers stay off until the
//     visitor accepts the banner.
//   - US / rest-of-world / unknown geo → implied accept; trackers behave exactly as
//     before (normal global experience, no banner).

import Cookies from 'js-cookie'
import {
  CONSENT_VERSION,
  CONSENT_REQUIRED_COUNTRIES,
  parseConsentRecord,
} from './consent-policy'
export { CONSENT_VERSION } from './consent-policy'

export const CONSENT_COOKIE = 'pc_consent'
export const CONSENT_STORAGE_KEY = 'pc_consent_v1'

// Window events: react to consent changes / re-open the banner from the footer.
export const CONSENT_CHANGED_EVENT = 'pc:consent-changed'
export const OPEN_CONSENT_EVENT = 'pc:open-consent'

function isBrowser() {
  return typeof window !== 'undefined'
}

// Region comes from middleware's geolocation cookie, independently of content
// preferences or administrator safe-country overrides.
// (Vercel `x-vercel-ip-country`, default 'ZZ').
export function getCountry() {
  if (!isBrowser()) return undefined
  return Cookies.get('pc_consent_country') || Cookies.get('country')
}

// Dev-only override to preview the EU banner without production geo headers.
// Toggle at runtime from devtools (no restart):
//   localStorage.setItem('pc_force_consent', '1')   // then reload → banner shows
//   localStorage.removeItem('pc_force_consent')      // then reload → back to normal
// Or set NEXT_PUBLIC_FORCE_CONSENT_BANNER=true in .env.local. Ignored in production.
export function isConsentDebugForced() {
  if (process.env.NODE_ENV === 'production') return false
  try {
    if (
      typeof window !== 'undefined' &&
      window.localStorage.getItem('pc_force_consent')
    ) {
      return true
    }
  } catch {
    // ignore
  }
  return process.env.NEXT_PUBLIC_FORCE_CONSENT_BANNER === 'true'
}

// Consent is required ONLY when we positively detect an EU/EEA/UK country. Unknown
// or missing geo ('ZZ' or absent — e.g. local dev, admin/whitelisted users whom
// middleware sets to 'ZZ', or a geo lookup failure) gets the normal global
// experience: no banner, trackers on. (`country` comes from Vercel geo via
// middleware, which is reliable in production.)
export function isConsentRequired(country = getCountry()) {
  if (isConsentDebugForced()) return true
  if (!country || country === 'ZZ') return false
  return CONSENT_REQUIRED_COUNTRIES.has(country.toUpperCase())
}

export function readConsent() {
  if (!isBrowser()) return null
  let raw = null
  try {
    raw = window.localStorage.getItem(CONSENT_STORAGE_KEY)
  } catch {
    // localStorage blocked — fall back to the mirror cookie below.
  }
  if (!raw) raw = Cookies.get(CONSENT_COOKIE) || null
  if (!raw) return null
  return parseConsentRecord(raw)
}

export function isConsentDecided() {
  return readConsent() !== null
}

export function writeConsent(
  analyticsConsent,
  region = isConsentRequired() ? 'EU' : 'OTHER',
) {
  if (!isBrowser()) return null
  const record = {
    version: CONSENT_VERSION,
    timestamp: new Date().toISOString(),
    region,
    analyticsConsent: !!analyticsConsent,
  }
  const serialized = JSON.stringify(record)
  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, serialized)
  } catch {
    // ignore — the mirror cookie below is enough for the gate to work.
  }
  Cookies.set(CONSENT_COOKIE, serialized, {
    expires: 180,
    sameSite: 'Lax',
    secure: window.location.protocol === 'https:',
  })
  emitConsentChanged()
  return record
}

// An explicit refusal applies everywhere. Without a decision, consent-required
// regions stay off and other regions use the existing implied-accept policy.
export function hasTrackingConsent() {
  const decision = readConsent()
  if (decision) return decision.analyticsConsent === true
  return !isConsentRequired()
}

// For non-EU visitors without a record yet, persist an implied-accept so the
// decision is explicit and stable.
// Returns whether tracking should be enabled right now.
export function resolveInitialConsent() {
  if (!isBrowser()) return false
  if (!isConsentRequired()) {
    if (!isConsentDecided()) writeConsent(true, 'OTHER')
    return hasTrackingConsent()
  }
  return readConsent()?.analyticsConsent === true
}

function emitConsentChanged() {
  if (!isBrowser()) return
  window.dispatchEvent(new Event(CONSENT_CHANGED_EVENT))
}

export function onConsentChanged(cb) {
  if (!isBrowser()) return () => {}
  const storageChanged = (event) => {
    if (event.key === CONSENT_STORAGE_KEY || event.key === null) cb()
  }
  window.addEventListener(CONSENT_CHANGED_EVENT, cb)
  window.addEventListener('storage', storageChanged)
  return () => {
    window.removeEventListener(CONSENT_CHANGED_EVENT, cb)
    window.removeEventListener('storage', storageChanged)
  }
}

// Footer "Cookie settings" re-opens the banner (also the withdrawal path).
export function openConsentSettings() {
  if (!isBrowser()) return
  window.dispatchEvent(new Event(OPEN_CONSENT_EVENT))
}

export function onOpenConsentSettings(cb) {
  if (!isBrowser()) return () => {}
  window.addEventListener(OPEN_CONSENT_EVENT, cb)
  return () => window.removeEventListener(OPEN_CONSENT_EVENT, cb)
}
